import React from 'react';
import { Printer, FlaskConical } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatCurrency, formatNumber } from '../../lib/utils';
import api from '../../lib/api';
import { toast } from 'sonner';

export function FormulaBreakdownDialog({ open, onClose, data, dispatchId }) {
  const handlePrint = async () => {
    try {
      const response = await api.get(`/dispatches/${dispatchId}/formula-breakdown/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('PDF generado');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="formula-breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-[#931A25]" />
            Desglose de Fórmula — {data.dispatch_number}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Desglose de ingredientes y costos de la fórmula usada en el despacho
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
          <span><b>Proyecto:</b> {data.project_name || '-'}</span>
          <span><b>Fecha:</b> {data.dispatch_date || '-'}</span>
          <span><b>Lote:</b> {data.lote_number || '-'}</span>
          <span><b>Nave:</b> {data.nave_number || '-'}</span>
          <span><b>Tasa:</b> RD$ {formatNumber(data.exchange_rate)}</span>
        </div>

        {data.items.length === 0 && (
          <p className="text-slate-500 py-4">Este despacho no contiene alimentos con fórmula asociada.</p>
        )}

        {data.items.map((entry, idx) => (
          <div key={idx} className="border border-slate-200 rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold text-[#931A25]">
                {entry.product_name} — {formatNumber(entry.quantity)} {entry.unit}
              </h3>
              {entry.formula_name && (
                <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                  Fórmula: <b>{entry.formula_name}</b> (produce {formatNumber(entry.output_quantity)} {entry.output_unit}) • Usado: {(entry.factor * 100).toFixed(2)}%
                </span>
              )}
            </div>

            {!entry.formula_name ? (
              <p className="text-sm text-slate-500">Sin fórmula vinculada a este producto.</p>
            ) : entry.formula_mismatch ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800" data-testid="formula-mismatch-notice">
                Desglose no disponible: este despacho se realizó con una versión anterior de la fórmula o con precios de materias primas diferentes a los actuales.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Ingrediente</TableHead>
                      <TableHead className="text-right">En Fórmula</TableHead>
                      <TableHead className="text-right">Usado</TableHead>
                      <TableHead className="text-right">Costo/lb</TableHead>
                      <TableHead className="text-right">Subtotal RD$</TableHead>
                      <TableHead className="text-right">Subtotal US$</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entry.ingredients.map((ing, i) => (
                      <TableRow key={i} className={ing.is_premix ? 'bg-purple-50/60' : ing.is_operational ? 'bg-orange-50/60' : ''}>
                        <TableCell className="font-medium">
                          <span className="flex items-center gap-1">
                            {ing.is_premix && (
                              <span className="text-[10px] bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded font-semibold" title="Premix">
                                PREMIX
                              </span>
                            )}
                            {ing.is_operational && (
                              <span className="text-[10px] bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded font-semibold" title="Costo operativo">
                                OP
                              </span>
                            )}
                            {ing.material_name}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {ing.is_operational
                            ? `${formatCurrency(ing.formula_qty)}/${entry.output_unit || 'qq'}`
                            : `${formatNumber(ing.formula_qty)} ${ing.unit}`}
                        </TableCell>
                        <TableCell className="text-right">
                          {ing.is_operational
                            ? `${formatNumber(ing.qty_used)} ${entry.output_unit || 'qq'}`
                            : `${formatNumber(ing.qty_used)} ${ing.unit}`}
                        </TableCell>
                        <TableCell className="text-right">
                          <div>{formatCurrency(ing.cost_per_lb_dop)}</div>
                          <div className="text-xs text-slate-500">{formatCurrency(ing.cost_per_lb_usd, 'USD')}</div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(ing.subtotal_dop)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(ing.subtotal_usd, 'USD')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex flex-col items-end gap-1 text-sm pr-2">
                  <span>Costo por {entry.output_unit}: <b>{formatCurrency(entry.unit_cost_dop)}</b> ({formatCurrency(entry.unit_cost_usd, 'USD')})</span>
                  <span className="text-[#931A25] font-semibold">
                    TOTAL despacho ({formatNumber(entry.quantity)} {entry.unit}): {formatCurrency(entry.dispatch_cost_dop)} ({formatCurrency(entry.dispatch_cost_usd, 'USD')})
                  </span>
                </div>
              </>
            )}
          </div>
        ))}

        <p className="text-xs text-slate-400">
          Nota: Los costos se calculan con los precios promedio actuales de las materias primas.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="breakdown-close-btn">Cerrar</Button>
          <Button className="bg-[#931A25] hover:bg-[#7A151F]" onClick={handlePrint} data-testid="breakdown-print-btn">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
